import { computed, ComputedRef, ref, Ref, ShallowRef, shallowRef, watch } from "vue";
import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { useFavouritesStore } from "@/shared/stores/use-favourites-store";
import { Event, EventDrilldown } from "@/shared/model/event";
import type { EventsDrilldownDetailEvent } from "@/live/model/events-drilldown-detail-event";
import useOpenbetApi from "@/api/composables/use-openbet-api";
import { MediaType } from "@/shared/model/enums/media-type";
import { LiveSportsTabs } from "@/shared/model/enums/live-sports-tabs";
import { LiveSportsDrilldownNode } from "@/live/model/live-sports-drilldown-nodes";
import { EventState } from "@/shared/model/enums/event-state";
import { DrilldownNode } from "@/shared/model/drilldown-node";
import useMock from "@/live/composables/use-mock";
import useMarketGrouping from "@/live/composables/use-market-grouping";
import {
    cloneDeep as _cloneDeep,
    findKey as _findKey,
    groupBy as _groupBy,
    isEmpty as _isEmpty,
    merge as _merge,
    uniq as _uniq,
    uniqBy as _uniqBy,
} from "lodash";
import { MarketPayload } from "@/shared/model/market";
import { DRILLDOWN_LEAGUE_LEVEL_NUMBER, DRILLDOWN_SPORT_LEVEL_NUMBER } from "@/shared/utils/constants";
import useUtils from "@/shared/composables/use-utils";
import { analyticsPageObj } from "@/shared/composables/analytics/use-general-entities";
import { Constants } from "@/shared/model/enums/analytics/constants";
import { waitUntilTrue } from "@/shared/utils/wait";
import { logException } from "@/shared/utils/logger/use-app-logger";
import { useRoute } from "vue-router";
import useIsBreakpoint from "@/shared/composables/use-is-breakpoint";
import type { EventCategories } from "@/live/model/event-categories";
import { RemoveOthers } from "@/api/interfaces/common";
import {
    createIdMap,
    excludeIdsFromIdMap,
    excludeOtherTypeIdsFromIdMap,
    getIdsByTypeFromIdMap,
    getIdsFromIdMap,
    getSportIdsFromIdMap,
} from "@/shared/utils/remove-others";

const eventHasVideoProvider = <T extends Event>(event: T): boolean => {
    return Boolean(event.mediaProviders?.some((provider) => provider.mediaType === MediaType.Video));
};

const findDisplayColumnIndexByGroupCode = (
    marketDropdown: { [columnIndex: number]: string },
    groupCode: string,
): string => _findKey(marketDropdown, (code) => code === groupCode);

const findPreviousHeaderIndex = (
    liveSport: LiveSportsDrilldownNode,
    displayColumnIndex: string,
    groupCode: string,
): number =>
    liveSport.oddsHeaders.findIndex(
        (header: MarketPayload) =>
            header.displayColumnIndex === Number(displayColumnIndex) && header.groupCode !== groupCode,
    );

export const processEventCategories = (
    events: EventsDrilldownDetailEvent[],
): EventCategories<EventsDrilldownDetailEvent>[] => {
    if (!Array.isArray(events)) return [];
    const categoriesMap = new Map();
    events.forEach((event) => {
        const id = event.competitionDrilldownTagId;
        let category = categoriesMap.get(id);
        if (category) {
            category.values.push(event);
        } else {
            category = { id, values: [event] };
            categoriesMap.set(id, category);
        }
    });
    return Array.from(categoriesMap.values());
};

const DEFAULT_LIVE_FETCH_INTERVAL = 10_000;

export const useLiveSportsStore = defineStore(
    "LiveSports",
    () => {
        const route = useRoute();
        const { isMobile } = useIsBreakpoint();
        // state
        const initialized: Ref<boolean> = ref(false);
        const fetchInterval: Ref<number> = ref(0);
        const activeTab: Ref<LiveSportsTabs> = ref(null); // persisted
        const liveSports: ShallowRef<DrilldownNode[]> = shallowRef(); // Do not change initial state to anything else (should stay undefined)... it's used for detection of initialization of the whole store
        const liveSportsTimestamp: Ref<number> = ref(0);
        const activeSportIds: Ref<string[]> = ref([]); // persisted
        const detailLeftSportId: Ref<string> = ref();
        const selectedMarketDropdowns: Ref<{ [sportId: string]: { [columnIndex: number]: string } }> = ref({});
        const marketPreviewConfigsBySportCode: Ref<{
            [key: string]: { marketGroupTypesIncluded: string[]; marketsSortsIncluded: string[] } | undefined;
        }> = ref();

        const { getEventsDrilldown, getSports } = useOpenbetApi();
        const { getHeaders, getOddsByHeaders } = useMarketGrouping();
        const { getMarketPreviewConfig, sportHasMarketPreviewConfig } = useMock();
        const { sortByDisplayOrder } = useUtils();

        const favouritesStore = useFavouritesStore();
        const { favouriteEvents: favouriteEventIds, favouriteLeagues: favouriteLeagueIds } =
            storeToRefs(favouritesStore);

        let fetchIntervalId = null;

        watch(
            fetchInterval,
            (newValue) => {
                if (newValue) {
                    fetchLiveSports();
                    if (fetchIntervalId) clearInterval(fetchIntervalId);
                    fetchIntervalId = setInterval(async () => {
                        await fetchLiveSports();
                    }, fetchInterval.value);
                } else {
                    if (fetchIntervalId) {
                        clearInterval(fetchIntervalId);
                        fetchIntervalId = null;
                    }
                }
            },
            { immediate: true },
        );

        // IdMap Helpers - we need to get additional information based or event ids (favourites) or by requirement that event has media provider (streams)
        // These data are pure 'throw-aways', only results in form of id maps are stored in store... we need to get proper data with correct market preview configs anyway (will be loaded later)
        const favouriteEventAndLeagueIdMap: Ref<RemoveOthers> = ref(null);
        watch([liveSports, favouriteEventIds, favouriteLeagueIds], async () => {
            if (!liveSports.value?.length || (!favouriteEventIds.value.length && !favouriteLeagueIds.value.length)) {
                favouriteEventAndLeagueIdMap.value = !Array.isArray(liveSports.value) ? null : {};
                return;
            }
            let eventIdMap = {};
            if (favouriteEventIds.value.length) {
                const favouriteEvents = await getEventsDrilldown({
                    eventIds: favouriteEventIds.value,
                    liveNow: true,
                });
                const favouriteEventsGrouped = _groupBy(favouriteEvents, "drilldownNodes[2].id");
                eventIdMap = createIdMap(favouriteEventsGrouped, "id");
            }
            let leagueIdMap = {};
            if (favouriteLeagueIds.value.length) {
                const favouriteLeagues = await getEventsDrilldown({
                    drilldownTagIds: favouriteLeagueIds.value,
                    liveNow: true,
                });
                const favouriteLeaguesGrouped = _groupBy(favouriteLeagues, "drilldownNodes[2].id");
                leagueIdMap = createIdMap(favouriteLeaguesGrouped, "drilldownNodes[0].id", "leagueIds");
            }
            favouriteEventAndLeagueIdMap.value = _merge(eventIdMap, leagueIdMap);
        });
        const streamedEventIdMap: Ref<RemoveOthers> = ref(null);
        watch(liveSports, async () => {
            if (!liveSports.value?.length) {
                streamedEventIdMap.value = !Array.isArray(liveSports.value) ? null : {};
                return;
            }
            const eventsWithStreams = await getEventsDrilldown({
                drilldownTagIds: liveSports.value.map((ls) => ls.id),
                liveNow: true,
                mediaTypesFilter: MediaType.Video,
            });
            const eventsWithStreamsGrouped = _groupBy(eventsWithStreams, "drilldownNodes[2].id");
            streamedEventIdMap.value = createIdMap(eventsWithStreamsGrouped, "id");
        });
        const favouriteEventDrilldowns: Ref<EventDrilldown[]> = ref(null);
        const isFavouriteEventDrilldownsLoading = shallowRef(false);
        watch(
            [liveSportsTimestamp, favouriteEventAndLeagueIdMap, activeTab],
            async ([liveSportsTimestampNew], [liveSportsTimestampOld]) => {
                if (
                    _isEmpty(favouriteEventAndLeagueIdMap.value) ||
                    (route.name === "Live" && activeTab.value !== LiveSportsTabs.Favourites)
                ) {
                    favouriteEventDrilldowns.value = null;
                    return;
                }
                if (liveSportsTimestampNew === liveSportsTimestampOld && favouriteEventDrilldowns.value !== null) {
                    return;
                }
                isFavouriteEventDrilldownsLoading.value = true;
                let leagueFavourites = [];
                const favouriteLeagueIdMap = excludeOtherTypeIdsFromIdMap(
                    favouriteEventAndLeagueIdMap.value,
                    "leagueIds",
                );
                if (!_isEmpty(favouriteLeagueIdMap)) {
                    const sportIds = getSportIdsFromIdMap(favouriteLeagueIdMap);
                    const leagueIds = getIdsByTypeFromIdMap(favouriteLeagueIdMap, "leagueIds");
                    const favouriteLeagueLiveSports = liveSports.value.filter((ls) => sportIds.includes(ls.id));
                    leagueFavourites = await fetchEventsDrilldown(
                        favouriteLeagueLiveSports,
                        leagueIds,
                        undefined,
                        sportIds,
                        favouriteLeagueIdMap,
                    );
                }
                // If favourite league contains favourite event it is not necessary to load that same data again, so we can filter it out
                let eventFavourites = [];
                const favouriteEventIdMap = excludeOtherTypeIdsFromIdMap(
                    favouriteEventAndLeagueIdMap.value,
                    "eventIds",
                );
                if (!_isEmpty(favouriteEventIdMap)) {
                    const leagueFavouritesEventIds = leagueFavourites.map((e) => e.id);
                    const favouriteEventIdMapFiltered = leagueFavouritesEventIds.length
                        ? excludeIdsFromIdMap(favouriteEventIdMap, leagueFavouritesEventIds)
                        : favouriteEventIdMap;
                    if (!_isEmpty(favouriteEventIdMapFiltered)) {
                        const sportIds = getSportIdsFromIdMap(favouriteEventIdMapFiltered);
                        const eventIds = getIdsByTypeFromIdMap(favouriteEventIdMapFiltered);
                        const favouriteEventLiveSports = liveSports.value.filter((ls) => sportIds.includes(ls.id));
                        // A bit hacky way how to merge event ids from leagues and events, but it'll do the job
                        const removeOthers = leagueFavouritesEventIds.length
                            ? _merge(favouriteEventIdMapFiltered, { league: { eventIds: leagueFavouritesEventIds } })
                            : favouriteEventIdMapFiltered;
                        eventFavourites = await fetchEventsDrilldown(
                            favouriteEventLiveSports,
                            undefined,
                            eventIds,
                            sportIds,
                            removeOthers,
                        );
                    }
                }

                favouriteEventDrilldowns.value = [].concat(eventFavourites, leagueFavourites);
                isFavouriteEventDrilldownsLoading.value = false;
            },
        );
        const streamedEventDrilldowns: Ref<EventDrilldown[]> = ref(null);
        const isStreamedEventDrilldownsLoading = shallowRef(false);
        watch(
            [liveSportsTimestamp, streamedEventIdMap, activeTab],
            async ([liveSportsTimestampNew], [liveSportsTimestampOld]) => {
                if (
                    _isEmpty(streamedEventIdMap.value) ||
                    route.name !== "Live" ||
                    activeTab.value !== LiveSportsTabs.Streams
                ) {
                    streamedEventDrilldowns.value = null;
                    return;
                }
                if (liveSportsTimestampNew === liveSportsTimestampOld && streamedEventDrilldowns.value !== null) {
                    return;
                }
                isStreamedEventDrilldownsLoading.value = true;
                const sportIds = getSportIdsFromIdMap(streamedEventIdMap.value);
                const eventIds = getIdsByTypeFromIdMap(streamedEventIdMap.value);
                const streamedLiveSports = liveSports.value.filter((ls) => sportIds.includes(ls.id));
                streamedEventDrilldowns.value = await fetchEventsDrilldown(
                    streamedLiveSports,
                    undefined,
                    eventIds,
                    sportIds,
                    streamedEventIdMap.value,
                );
                isStreamedEventDrilldownsLoading.value = false;
            },
        );
        const otherEventDrilldowns: Ref<EventDrilldown[]> = ref(null);
        const isOtherEventDrilldownsLoading = shallowRef(false);
        watch(
            [liveSportsTimestamp, liveSports, activeTab, activeSportIds, detailLeftSportId],
            async (
                [liveSportsTimestampNew, liveSportsNew, activeTabNew, activeSportIdsNew, detailLeftSportIdNew],
                [liveSportsTimestampOld, liveSportsOld, activeTabOld, activeSportIdsOld, detailLeftSportIdOld],
            ) => {
                if (!liveSports.value?.length || activeTab.value !== LiveSportsTabs.OtherSports) {
                    otherEventDrilldowns.value = null;
                    return;
                }
                if (
                    liveSportsTimestampNew === liveSportsTimestampOld &&
                    otherEventDrilldowns.value !== null &&
                    otherEventDrilldowns.value.some((event) =>
                        event.drilldownNodes.some((node) => node.id === activeSportIdsNew?.[0]),
                    )
                ) {
                    return;
                }
                isOtherEventDrilldownsLoading.value = true;
                const sportIdsWithWsData = route.name === "Live" ? activeSportIds.value : [detailLeftSportId.value];
                const drilldownNodes = liveSports.value.filter((ls) =>
                    route.name === "Live" ? activeSportIds.value.includes(ls.id) : detailLeftSportId.value === ls.id,
                );
                const removeOthers =
                    route.name === "Live"
                        ? activeSportIdsNew !== activeSportIdsOld
                        : detailLeftSportIdNew !== detailLeftSportIdOld;
                otherEventDrilldowns.value = !drilldownNodes?.length
                    ? []
                    : await fetchEventsDrilldown(
                          drilldownNodes,
                          undefined,
                          undefined,
                          sportIdsWithWsData,
                          removeOthers,
                      );
                isOtherEventDrilldownsLoading.value = false;
            },
        );

        // getters
        const hasFavourites = computed(() => !_isEmpty(favouriteEventAndLeagueIdMap.value));
        const hasStreams = computed(() => !_isEmpty(streamedEventIdMap.value));
        const favouritesCount = computed(
            () =>
                getIdsFromIdMap(favouriteEventAndLeagueIdMap.value, "eventIds").length +
                getIdsFromIdMap(favouriteEventAndLeagueIdMap.value, "leagueIds").length,
        );
        const streamsCount = computed(() => getIdsFromIdMap(streamedEventIdMap.value).length);
        const sportFilterData: ComputedRef<LiveSportsDrilldownNode[]> = computed(() => {
            const retData =
                liveSports.value
                    ?.map(
                        (drilldownNode: DrilldownNode) =>
                            ({
                                ...drilldownNode,
                                count:
                                    otherSports.value?.find((sport) => sport.id === drilldownNode.id)?.events?.length ||
                                    // Fallback for other sports than currently displayed
                                    drilldownNode?.eventCounts?.find((count) => Object.keys(count)[0] === "LIVE")?.[
                                        "LIVE"
                                    ] ||
                                    0,
                            }) as unknown as LiveSportsDrilldownNode,
                    )
                    .filter((drilldownNode) => drilldownNode.count > 0) || [];
            return favouritesStore.sortLiveSportsDrilldownNodes(retData);
        });

        const favouriteSports: ComputedRef<LiveSportsDrilldownNode[]> = computed(() => {
            if (!liveSports.value?.length || !favouriteEventDrilldowns.value?.length) return [];
            const favouriteLiveSports = liveSports.value.filter((ls) =>
                getSportIdsFromIdMap(favouriteEventAndLeagueIdMap.value).includes(ls.id),
            );
            const sportsAll: LiveSportsDrilldownNode[] = [];
            favouriteLiveSports.forEach((liveSport) =>
                sportsAll.push(
                    processSport(
                        liveSport,
                        favouriteEventDrilldowns.value?.filter((event) =>
                            favouritesStore.isEventInFavouriteEventsOrLeagues(event),
                        ) || [],
                        true,
                    ),
                ),
            );
            return sportsAll;
        });
        const streamedSports: ComputedRef<LiveSportsDrilldownNode[]> = computed(() => {
            if (!liveSports.value?.length || !streamedEventDrilldowns.value?.length) return [];
            const streamedLiveSports = liveSports.value.filter((ls) =>
                getSportIdsFromIdMap(streamedEventIdMap.value).includes(ls.id),
            );
            const sportsAll: LiveSportsDrilldownNode[] = [];
            streamedLiveSports.forEach((liveSport) =>
                sportsAll.push(
                    processSport(
                        liveSport,
                        streamedEventDrilldowns.value?.filter((event) => eventHasVideoProvider(event)) || [],
                        true,
                    ),
                ),
            );
            return sportsAll;
        });
        const otherSports: ComputedRef<LiveSportsDrilldownNode[]> = computed(() => {
            // Once liveSports changes its value from undefined to array (means data are loaded) eventDrilldownsByActiveTab is accessed.
            // Because lazy loading is set (it is not evaluated on initialization but on access) this means it'll not change it's initial value until accessed.
            // Keep in mind that this should be the first place where eventDrilldownsByActiveTab should be accessed... otherwise the initialization logic will NOT work.
            if (
                !liveSports.value?.length ||
                (route.name === "Live" && (!otherEventDrilldowns.value?.length || !activeSportIds.value.length))
            ) {
                return [];
            }
            const otherLiveSports =
                route.name === "Live"
                    ? liveSports.value.filter((ls) => activeSportIds.value.includes(ls.id))
                    : liveSports.value;
            const sportsAll: LiveSportsDrilldownNode[] = [];
            otherLiveSports.forEach((liveSport) =>
                sportsAll.push(processSport(liveSport, otherEventDrilldowns.value, true)),
            );
            return sportsAll;
        });
        const sportDataByActiveTab: ComputedRef<LiveSportsDrilldownNode[]> = computed(() => {
            switch (activeTab.value) {
                case LiveSportsTabs.Favourites:
                    return favouriteSports.value;
                case LiveSportsTabs.Streams:
                    return streamedSports.value;
                case LiveSportsTabs.OtherSports:
                    return otherSports.value;
                default:
                    return [];
            }
        });

        const eventDrilldownsByActiveTab: Ref<EventDrilldown[]> = computed(() => {
            if (
                favouriteEventDrilldowns.value === null &&
                streamedEventDrilldowns.value === null &&
                otherEventDrilldowns.value === null
            ) {
                return null;
            }
            switch (activeTab.value) {
                case LiveSportsTabs.Favourites:
                    return favouriteEventDrilldowns.value;
                case LiveSportsTabs.Streams:
                    return streamedEventDrilldowns.value;
                case LiveSportsTabs.OtherSports:
                    return otherEventDrilldowns.value;
                default:
                    return null;
            }
        });

        watch(
            eventDrilldownsByActiveTab,
            (newValue) => {
                if (Array.isArray(newValue)) {
                    initialized.value = true;
                }
            },
            { once: true },
        );

        // If there is no event in progress change focus to first suitable element in the filter
        watch(
            () =>
                initialized.value &&
                activeTab.value &&
                Array.isArray(eventDrilldownsByActiveTab.value) &&
                !eventDrilldownsByActiveTab.value.length,
            (newValue) => {
                if (newValue) {
                    setActiveTab();
                }
            },
        );
        // We need to detect when activeTab is set to null ASAP and set the new active tab hence this watcher
        watch(activeTab, (newValue, oldValue) => {
            if (initialized.value && !newValue && oldValue !== undefined) {
                setActiveTab();
            }
        });

        // helpers
        const filterOddsBySelectedMarkets = (ls: LiveSportsDrilldownNode[]): LiveSportsDrilldownNode[] => {
            const liveSports = _cloneDeep(ls);
            // Filter odds by selected market dropdowns/chips
            if (!_isEmpty(selectedMarketDropdowns.value) && liveSports.length > 0) {
                const liveSportIds: string[] = Object.keys(selectedMarketDropdowns.value);
                liveSports.forEach((liveSport) => {
                    if (!liveSportIds.includes(liveSport.id)) return;
                    const marketDropdown = selectedMarketDropdowns.value[liveSport.id];
                    if (!marketDropdown) return;
                    liveSport.oddsHeaders.forEach((m) => {
                        const displayColumnIndex = findDisplayColumnIndexByGroupCode(marketDropdown, m.groupCode);
                        if (!displayColumnIndex) return;
                        const prevDisplayColumnIndex = m.displayColumnIndex;
                        m.displayColumnIndex = Number(displayColumnIndex);
                        const prevHeaderIdx = findPreviousHeaderIndex(liveSport, displayColumnIndex, m.groupCode);
                        if (prevHeaderIdx > -1) {
                            liveSport.oddsHeaders[prevHeaderIdx].displayColumnIndex = prevDisplayColumnIndex;
                        }
                    });

                    liveSport.events.forEach((event) => {
                        event.oddsByHeaders = getOddsByHeaders(liveSport, event);
                    });
                });
            }
            return liveSports;
        };
        // markety jeden sport - mel by se posilat config podle toho sportu do sazka-events-drilldown-list ktery ted nefunguje.
        // Config rika ktere markety podle ?market_group_code? ma presne vracet.
        // Prozatim mame config tak udelame na FE.
        // do 1024 jsou to chipy, od 1024 - podle viewportu pocet dropdownu
        const processSport = (
            drilldownNode: DrilldownNode,
            events: EventDrilldown[],
            immediateGrouping = false,
        ): LiveSportsDrilldownNode => {
            const liveSport = { ...drilldownNode } as unknown as LiveSportsDrilldownNode;
            if (route.name !== "Live" && !events?.length) return liveSport;

            const activeEvents = events.filter((e) =>
                e?.drilldownNodes?.some(
                    (node) => node.id === liveSport.id && node.levelNumber === DRILLDOWN_SPORT_LEVEL_NUMBER,
                ),
            );

            const sortedEvents = sortByDisplayOrder(activeEvents) as EventsDrilldownDetailEvent[];

            liveSport.drilldownNodes = _uniqBy(
                _cloneDeep(sortedEvents).flatMap((e) => e.drilldownNodes),
                "id",
            );

            if (immediateGrouping) {
                processActiveSportsGrouping(liveSport, sortedEvents, true);
            }
            return liveSport;
        };
        const processActiveSportsGrouping = (
            liveSport: LiveSportsDrilldownNode,
            events: EventDrilldown[],
            refresh: boolean = false,
        ) => {
            if (!liveSport || !events) return;
            const newEvents: EventsDrilldownDetailEvent[] = [];
            events.forEach((event: EventDrilldown) => {
                newEvents.push({
                    ...event,
                } as EventsDrilldownDetailEvent);
            });

            // TODO: Watch and if nothing is broken refactor (usage of LiveSports store inside getHeaders() creates overflow of callstack)
            //liveSport.oddsHeaders = getHeaders(_cloneDeep(activeSports.value), liveSport, refresh);
            liveSport.oddsHeaders = getHeaders(
                [],
                { ...liveSport, events: newEvents } as LiveSportsDrilldownNode,
                refresh,
            );
            newEvents.forEach((event: EventsDrilldownDetailEvent) => {
                event.oddsByHeaders = getOddsByHeaders(liveSport, event);
            });
            liveSport.events = newEvents;

            // Add odds headers to every league
            liveSport.drilldownNodes.forEach((league) => {
                if (league.levelNumber !== DRILLDOWN_LEAGUE_LEVEL_NUMBER) return;
                const leagueEvents = events.filter((event) => event?.drilldownNodes[0].id == league.id);

                league.oddsHeaders = getHeaders(
                    [],
                    { ...liveSport, events: leagueEvents } as LiveSportsDrilldownNode,
                    refresh,
                );
            });
        };

        // actions
        const waitUntilInitialized = async (time: number = 100, timeout: number = 10000) => {
            await waitUntilTrue(initialized, time, timeout).catch(() => {
                logException("live-sports", undefined, {
                    message: `Store initialization timed out. Timeout interval ${timeout} breached.`,
                });
            });
        };
        const setFetchInterval = (interval: number) => {
            fetchInterval.value = interval;
        };
        const fetchLiveSports = async () => {
            const sports: DrilldownNode[] =
                (await getSports({
                    eventState: EventState.LiveEvent,
                })) || [];
            liveSports.value = sports;
            liveSportsTimestamp.value = performance.now();
            return sports;
        };
        const fetchEventsDrilldown = async (
            drilldownNodes: DrilldownNode[],
            leagueIdsMap?: { [sportId: string]: string[] },
            eventIdsMap?: { [sportId: string]: string[] },
            sportIdsWithWsData?: string[],
            removeOthers?: boolean | RemoveOthers,
        ): Promise<EventDrilldown[]> => {
            const eventDrilldownPromises = () => {
                const promises: Promise<EventDrilldown[]>[] = [];
                // It's more performant to load all events with no config at once in one call. There is one big BUT... ishtty OB endpoint!
                // We're risking that none of the events will be loaded for given drilldown node ids hence we need to divide it into separate calls. :(
                drilldownNodes.forEach((node) => {
                    const drilldownTagIds = leagueIdsMap?.[node.id]?.length ? leagueIdsMap?.[node.id] : [node.id];
                    const eventIds = eventIdsMap?.[node.id]?.length ? eventIdsMap?.[node.id] : undefined;

                    const { marketGroupTypesIncluded, marketsSortsIncluded } = marketPreviewConfigsBySportCode.value?.[
                        node.code
                    ] || { marketGroupTypesIncluded: undefined, marketsSortsIncluded: undefined };
                    promises.push(
                        getEventsDrilldown(
                            {
                                drilldownTagIds,
                                eventIds,
                                liveNow: true,
                                marketGroupTypesIncluded,
                                marketsSortsIncluded,
                                groupedMarkets: true,
                                // TODO: Enable once there will be in useOpenbetApi() composable implemented support for it
                                // oddsHeadersSports: true,
                            },
                            {
                                wsData: {
                                    update: sportIdsWithWsData?.includes(node.id),
                                    removeOthers: removeOthers ?? true,
                                },
                            },
                        ),
                    );
                });
                return promises;
            };

            const results = await Promise.allSettled(eventDrilldownPromises());
            let eventsAll: EventDrilldown[] = [];
            results.forEach((result) => {
                if (result.status === "fulfilled") {
                    eventsAll = eventsAll.concat((result as PromiseFulfilledResult<EventDrilldown[]>).value);
                }
            });

            return eventsAll;
        };
        const setActiveFavourites = () => {
            activeTab.value = LiveSportsTabs.Favourites;
            activeSportIds.value = [];
        };
        const setActiveStreams = () => {
            activeTab.value = LiveSportsTabs.Streams;
            activeSportIds.value = [];
        };
        const addActiveSportId = (id: string) => {
            if (!activeSportIds.value.includes(id)) activeSportIds.value.push(id);
        };
        const removeActiveSportId = (id: string) => {
            activeSportIds.value.splice(activeSportIds.value.findIndex((i) => i === id));
        };
        const setActiveSportId = (id: string) => {
            activeTab.value = LiveSportsTabs.OtherSports;
            activeSportIds.value = [id];
        };
        const setActiveTab = () => {
            if (activeTab.value === LiveSportsTabs.Favourites && !_isEmpty(favouriteEventAndLeagueIdMap.value)) {
                setActiveFavourites();
            } else if (activeTab.value === LiveSportsTabs.Streams && !_isEmpty(streamedEventIdMap.value)) {
                setActiveStreams();
            } else if (
                [LiveSportsTabs.OtherSports, null].includes(activeTab.value) &&
                sportFilterData.value.length > 0
            ) {
                setActiveSportId(
                    sportFilterData.value.map((s) => s.id).includes(activeSportIds.value[0])
                        ? activeSportIds.value[0]
                        : sportFilterData.value[0].id,
                );
            } else {
                activeTab.value = null;
                activeSportIds.value = [];
            }
        };

        const marketDropdownSelected = (sportId: string, columnIndex: number, marketType: MarketPayload) => {
            if (!selectedMarketDropdowns.value[sportId]) {
                selectedMarketDropdowns.value[sportId] = {};
            }
            selectedMarketDropdowns.value[sportId][columnIndex] = marketType.groupCode;
        };

        watch(activeSportIds, async (value) => {
            if (route.name !== "Live") return;
            if (value && value.length == 1) {
                await waitUntilInitialized();
                const gamecode = liveSports.value?.find((sport) => sport.id === value[0])?.code || Constants.NoGame;
                analyticsPageObj.gameCode = gamecode;
                analyticsPageObj.gameCodeStrict = gamecode;
            } else {
                analyticsPageObj.gameCode = Constants.NoGame;
                analyticsPageObj.gameCodeStrict = Constants.NoGame;
            }
        });

        watch(
            route,
            (newValue) => {
                setFetchInterval(
                    newValue.name === "Live" || (newValue.name === "LiveEventDetail" && !isMobile.value)
                        ? DEFAULT_LIVE_FETCH_INTERVAL
                        : 0,
                );
            },
            { immediate: true },
        );

        // Calculate market configs only when list of live sports changes
        watch(liveSportsTimestamp, (newValue, oldValue) => {
            if (newValue === oldValue) return;
            const config = {};
            const liveSportCodes = liveSports.value?.map((l) => l.code) || [];
            liveSportCodes.forEach((code) => {
                if (!sportHasMarketPreviewConfig(code)) {
                    config[code] = undefined;
                } else {
                    const sportConfig = getMarketPreviewConfig(code);
                    const marketGroupTypesIncluded = _uniq(sportConfig.map((market) => market.market_group_code));
                    const marketsSortsIncluded = _uniq(sportConfig.map((market) => market.sort_code));
                    config[code] = { marketGroupTypesIncluded, marketsSortsIncluded };
                }
            });
            marketPreviewConfigsBySportCode.value = config;
        });

        // set active tab immediately once we have data after initialization,
        // in case there will be persisted some sport id without events at the time of initialization default (first available sport) will be set instead
        const unwatch = watch(
            () => initialized.value || (activeTab.value === null && sportFilterData.value.length > 0),
            (newValue) => {
                if (newValue) {
                    setActiveTab();
                    unwatch();
                }
            },
        );

        return {
            // state
            initialized,
            fetchInterval,
            activeTab,
            liveSports,
            activeSportIds,
            detailLeftSportId,
            selectedMarketDropdowns,
            favouriteEventAndLeagueIdMap,
            streamedEventIdMap,
            favouriteEventDrilldowns,
            streamedEventDrilldowns,
            otherEventDrilldowns,
            isOtherEventDrilldownsLoading,
            isStreamedEventDrilldownsLoading,
            isFavouriteEventDrilldownsLoading,
            // getters
            hasFavourites,
            hasStreams,
            favouritesCount,
            streamsCount,
            sportFilterData,
            favouriteSports,
            streamedSports,
            otherSports,
            sportDataByActiveTab,
            eventDrilldownsByActiveTab,
            // helpers
            filterOddsBySelectedMarkets,
            processSport,
            processActiveSportsGrouping,
            // actions
            waitUntilInitialized,
            setFetchInterval,
            fetchLiveSports,
            fetchEventsDrilldown,
            setActiveFavourites,
            setActiveStreams,
            addActiveSportId,
            removeActiveSportId,
            setActiveSportId,
            setActiveTab,
            marketDropdownSelected,
        };
    },
    {
        persist: {
            pick: ["activeTab", "activeSportIds"],
            storage: localStorage,
        },
    },
);

// Enable HMR
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useLiveSportsStore, import.meta.hot));
}
